// /about — content-as-data per spec.
// Source: /content/marketing/about.json (with optional backend override at
// GET /api/marketing/about, mirroring the program merge pattern).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { C } from "@/lib/brand";
import { apiGet } from "@/lib/api";
import aboutContent from "@/content/marketing/about.json";

export default function About() {
  const [content, setContent] = useState(aboutContent);

  useEffect(() => {
    apiGet("/marketing/about", { auth: false })
      .then((backend) => backend && setContent({ ...aboutContent, ...backend }))
      .catch(() => {});
  }, []);

  const { hero, frustration, pivot, people, standard, bridgeToPrograms } = content;

  return (
    <div data-testid="marketing-about" style={{ background: C.cream }}>
      <PublicNav />

      <section style={{ background: C.cream, padding: "120px 24px 96px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {hero.eyebrow && <Eyebrow color={C.peachDeep}>{hero.eyebrow}</Eyebrow>}
          <Headline before={hero.headlineLine1} italic={hero.headlineLine2} size={84} className="mt-6" />
          {hero.subhead && (
            <p style={{ marginTop: 32, fontSize: 18, lineHeight: 1.6, maxWidth: 720, color: C.warmGray }}>
              {hero.subhead}
            </p>
          )}
        </div>
      </section>

      {frustration && <NarrativeBlock block={frustration} bg={C.bone} wedge wedgeFrom={C.cream} />}

      {pivot && (
        <section style={{ background: C.cream, padding: "96px 24px", position: "relative" }}>
          <TopWedge color={C.bone} dir="right" />
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
            <Eyebrow color={C.peachDeep}>{pivot.eyebrow}</Eyebrow>
            <Headline before={pivot.headlineLine1} italic={pivot.headlineLine2} size={64} className="mt-4" />
            {pivot.intro && (
              <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.7, maxWidth: 720, color: C.warmGray }}>
                {pivot.intro}
              </p>
            )}
            <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "1fr", gap: 28 }} className="md:!grid-cols-3">
              {(pivot.principles || []).map((p, i) => (
                <div key={i} style={{ borderTop: `2px solid ${C.peachDeep}`, paddingTop: 22 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>
                    {String(i + 1).padStart(2, "0")} · {p.title}
                  </div>
                  <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: C.warmGray }}>{p.body}</p>
                </div>
              ))}
            </div>
            {pivot.outro && (
              <p style={{ marginTop: 40, fontSize: 16, lineHeight: 1.65, color: C.ink, fontStyle: "italic", fontFamily: '"Instrument Serif", Georgia, serif', maxWidth: 720 }}>
                {pivot.outro}
              </p>
            )}
          </div>
        </section>
      )}

      {people && <NarrativeBlock block={people} bg={C.bone} />}

      {standard && (
        <section style={{ background: C.peach, padding: "96px 24px", position: "relative" }}>
          <TopWedge color={C.bone} dir="left" />
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2, color: C.peachDark }}>
            <Eyebrow color={C.peachDark}>{standard.eyebrow}</Eyebrow>
            <Headline before={standard.headlineLine1} italic={standard.headlineLine2} size={56} className="mt-4" italicColor={C.ink} />
            <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "1fr", gap: 28 }} className="md:!grid-cols-3">
              {(standard.columns || []).map((p, i) => (
                <div key={i} style={{ background: C.pure, padding: 28, color: C.ink }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>{p.title}</div>
                  <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: C.warmGray }}>{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {bridgeToPrograms && (
        <section style={{ background: C.ink, color: C.cream, padding: "96px 24px", textAlign: "center" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <Eyebrow color={C.peach}>{bridgeToPrograms.eyebrow}</Eyebrow>
            <Headline before={bridgeToPrograms.headlineLine1} italic={bridgeToPrograms.headlineLine2} color={C.cream} italicColor={C.peach} size={56} className="mt-4" />
            {bridgeToPrograms.body && (
              <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.6, color: "rgba(250,246,240,0.7)" }}>
                {bridgeToPrograms.body}
              </p>
            )}
            <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              {bridgeToPrograms.primaryCta && (
                <Link to={bridgeToPrograms.primaryCta.url} data-testid="about-back-home" style={{ textDecoration: "none" }}>
                  <Button variant="primary" size="lg">{bridgeToPrograms.primaryCta.label}</Button>
                </Link>
              )}
              {bridgeToPrograms.secondaryCta && (
                <Link to={bridgeToPrograms.secondaryCta.url} data-testid="about-get-access" style={{ textDecoration: "none" }}>
                  <Button variant="ghost" size="lg">{bridgeToPrograms.secondaryCta.label}</Button>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      <Footer prevColor={C.ink} wedgeDir="right" />
    </div>
  );
}

function NarrativeBlock({ block, bg, wedge, wedgeFrom }) {
  return (
    <section style={{ background: bg, padding: "96px 24px", position: "relative" }}>
      {wedge && <TopWedge color={wedgeFrom || C.cream} dir="left" />}
      <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
        {block.eyebrow && <Eyebrow color={C.peachDeep}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline
            before={block.headlineLine1 || block.headline}
            italic={block.headlineLine2 || ""}
            size={64}
            className="mt-4"
          />
        )}
        {block.body && (
          <div style={{ marginTop: 28, fontSize: 16, lineHeight: 1.7, color: C.warmGray, whiteSpace: "pre-line", maxWidth: 720 }}>
            {block.body}
          </div>
        )}
      </div>
    </section>
  );
}
